import { useEffect, useMemo, useCallback, useState } from 'react'

// MUI
import { Box, Button, CircularProgress } from '@mui/material'

// Components
import PDFSettingsModal from './PDFSettingsModal/PDFSettingsModal'
import ProcedureDetailsReportPDF from './ProcedureDetailsReport/ProcedureDetailsReportPDF'
import Header from 'components/molecules/Header/Header'
import { FilterBar } from 'components/organisms/FilterBar/FilterBar'
import { DataTable } from 'components/molecules/DataTable/DataTable'
import { ReadMore, Download, CardMembership } from '@mui/icons-material'
import WalletCardModal from './WalletCardModal/WalletCardModal'

// Utils
import { getAssetTypeLabel } from 'lib/utils/getAssetTypeLabel'
import currencyFormatter from 'lib/utils/currencyFormatter'

// Context
import { useReportFiltersContext } from 'lib/context/ReportFiltersContext/ReportFiltersContext'

// Services
import {
  useGetSurgeryDetailsMutation,
  useListSurgeries,
} from 'lib/services/api/reports-service/getUsageReports'

// Router
import { useNavigate } from 'react-router-dom'
import { PATIENT_REPORT } from 'app/ROUTES_CONSTANTS'

// Types
import { DefaultFilterParams } from 'lib/context/ReportFiltersContext/ReportFiltersContext.types'
import {
  SurgeriesOutput,
  MappedSurgeries,
  SurgeryDetailsOutput,
  GetUsageReportsVariables,
} from './PatientReport.types'
import { AssetTypeLabel } from 'common/types'

// Styles
import './PatientReport.scss'

// Other
import { saveAs } from 'file-saver'
import { pdf } from '@react-pdf/renderer'
import { enqueueSnackbar } from 'notistack'
import { headCells } from './constants'
import dayjs from 'lib/dayjs'
import { useUser } from 'app/User'
import { getOrganization } from 'lib/services/restApi/organizations/organizations'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useEnterpriseStore } from 'store'

const PatientReportsPage = () => {
  const [surgeryId, setSurgeryId] = useState<string | null>(null)
  const [walletCardModalOpen, setWalletCardModalOpen] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState<{
    fullName?: string
    email?: string
    phoneNumber?: string
    surgeryId: string
    patientMRN?: string
  } | null>(null)

  const { orgId } = useUser()
  const flags = useFlags()
  const {
    beforeDate,
    afterDate,
    filters,
    setFilters,
    setReportData,
    pdfSettings,
    setPDFSettings,
  } = useReportFiltersContext()

  const [pdfModalOpen, setPdfModalOpen] = useState(false)
  const navigate = useNavigate()
  const {
    mutateAsync: getSurgeryDetails,
    isLoading: isLoadingGetSurgeryDetails,
  } = useGetSurgeryDetailsMutation()

  const enableSendPatientWalletCard = true || flags.sendPatientWalletCard

  const { selectedDepartment } = useEnterpriseStore()

  const queryParams = useMemo(() => {
    const usageFilters = (filters as DefaultFilterParams) || {}
    const params = {} as GetUsageReportsVariables

    params.departmentId = selectedDepartment?.id
    if (usageFilters.mrn) params.mrn = usageFilters.mrn
    if (usageFilters.patientName) params.patientName = usageFilters.patientName
    if (usageFilters.surgeonName) params.surgeonName = usageFilters.surgeonName
    if (usageFilters.procedureType)
      params.procedureType = usageFilters.procedureType
    if (usageFilters.department) params.location = usageFilters.department
    if (afterDate)
      params.startDate = afterDate.format(
        'YYYY-MM-DD'
      ) as unknown as dayjs.Dayjs
    if (beforeDate)
      params.endDate = beforeDate.format('YYYY-MM-DD') as unknown as dayjs.Dayjs
    params.pageSize = 1000
    params.status = 'SUBMITTED'

    return params
  }, [filters, selectedDepartment?.id, afterDate, beforeDate])

  const { data: surgeries, isLoading, error } = useListSurgeries(queryParams)

  // Handlers
  const handlePrint = useCallback(
    async (
      surgeryId: string,
      details: SurgeryDetailsOutput,
      isAdventist: boolean
    ) => {
      try {
        if (details?.assetGroups?.length > 0) {
          const pdfDoc = pdf(
            <ProcedureDetailsReportPDF
              surgeryDetails={details}
              isAdventist={isAdventist}
            />
          )
          const blob = await pdfDoc.toBlob()
          saveAs(blob, `procedure-report-${surgeryId}.pdf`)
          enqueueSnackbar('PDF generated successfully', {
            variant: 'success',
          })
        } else {
          enqueueSnackbar('This surgery has no documented implants.', {
            variant: 'error',
          })
        }
      } catch (error) {
        enqueueSnackbar('Error generating PDF', {
          variant: 'error',
        })
        console.error('Error generating PDF:', error)
      }
      setSurgeryId(null)
    },
    []
  )

  const handleDownload = useCallback(
    async (surgeryId: string) => {
      try {
        setSurgeryId(surgeryId)
        const details = await getSurgeryDetails(surgeryId)
        if (details) {
          const assetTypes = pdfSettings.selectedAssetTypes

          const filteredDetails = details.assetGroups
            .map((assetGroup) => ({
              ...assetGroup,
              scans: assetGroup.scans.filter(
                (scan) =>
                  assetTypes?.includes(getAssetTypeLabel(scan.assetType)) &&
                  !(
                    scan.implantStatus === 'EXPLANTED' ||
                    scan.implantStatus === 'NOT_PRESENT'
                  )
              ),
            }))
            .filter((group) => group.scans.length > 0)

          const isAdventist = await getOrganization(orgId as string).then(
            ({ success, data }) => {
              if (success && data) {
                if (
                  data.auth0OrganizationName === 'Adventist' ||
                  data.auth0OrganizationName === 'MedGEO'
                ) {
                  return true
                }
              }
              return false
            }
          )

          await handlePrint(
            surgeryId,
            {
              ...details,
              assetGroups: filteredDetails,
            },
            isAdventist
          )
        } else {
          enqueueSnackbar(
            'This surgery has no documented implants. Try to add consumables from the PDF settings.',
            {
              variant: 'error',
            }
          )
        }
      } catch (error) {
        console.error('Error downloading surgery details:', error)
        enqueueSnackbar('Error retrieving surgery details', {
          variant: 'error',
        })
      }
    },
    [pdfSettings.selectedAssetTypes, orgId, handlePrint]
  )

  const handleOpenWalletCardModal = (surgery: SurgeriesOutput) => {
    // Extract patient info from the surgery data
    const patientName = `${surgery?.patientLastName}, ${surgery?.patientFirstName}`

    // Set patient information that will be passed to the wallet card modal
    setSelectedPatient({
      fullName: patientName,
      email: '',
      phoneNumber: '',
      surgeryId: surgery?.surgeryId || '',
      patientMRN: surgery?.patientMRN || '',
    })
    setSurgeryId(surgery?.surgeryId)
    setWalletCardModalOpen(true)
  }

  const handleAssetTypeChange = (assetTypes: AssetTypeLabel[]) => {
    setPDFSettings({
      ...pdfSettings,
      selectedAssetTypes: assetTypes,
    })
    setPdfModalOpen(false)
  }

  const hasImplantedHardware = (surgery: SurgeriesOutput): boolean => {
    return surgery.assets?.some((asset) => asset.implanted > 0) ?? false
  }

  useEffect(() => {
    if (surgeries) {
      setReportData(surgeries)
    }
  }, [surgeries, setReportData])

  useEffect(() => {
    setFilters({
      open: false,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const mappedData: MappedSurgeries[] = (surgeries || [])?.map(
    (surgery: SurgeriesOutput) => {
      const baseData = {
        date: dayjs(surgery?.procedures[0].dateTime).format('MMM DD, YYYY'),
        patient: `${surgery?.patientLastName}, ${surgery?.patientFirstName}`,
        // caseId: surgery?.surgeryId,
        mrn: surgery?.patientMRN || '-',
        surgeon: surgery?.surgeonLastName || '-',
        procedureType: surgery?.procedures[0]?.description || '-',
        totalSurgeryCost: surgery?.totalCost
          ? currencyFormatter.format(Number(surgery?.totalCost))
          : '-',
        download: (
          <Button
            onClick={() => handleDownload(surgery?.surgeryId)}
            disabled={
              isLoadingGetSurgeryDetails && surgeryId === surgery?.surgeryId
            }
          >
            {isLoadingGetSurgeryDetails && surgeryId === surgery?.surgeryId ? (
              <CircularProgress size={20} />
            ) : (
              <Download />
            )}
          </Button>
        ),
        caseDetails: (
          <Button
            onClick={() => {
              navigate(`/${PATIENT_REPORT}/${surgery?.surgeryId}`)
            }}
          >
            <ReadMore />
          </Button>
        ),
      }

      // Conditionally add wallet card column based on feature flag
      if (enableSendPatientWalletCard) {
        return {
          ...baseData,
          sendWalletCard: (
            <Button
              onClick={() => handleOpenWalletCardModal(surgery)}
              disabled={!hasImplantedHardware(surgery)}
            >
              <CardMembership />
            </Button>
          ),
        }
      }

      return baseData
    }
  )

  // Filter table headers based on feature flag
  const filteredHeadCells = enableSendPatientWalletCard
    ? headCells
    : headCells.filter((cell) => cell.id !== 'sendWalletCard')

  return (
    <Box className="usage-reports-container">
      <Header parent="Reports" title="Patients Report" />
      <FilterBar
        hasPDFSettings={true}
        renderMrnFilter
        renderPatientNameFilter
        renderSurgeonFilter
        renderProcedureTypeFilter
        renderDatePicker
        condenseButtons
        isLoading={isLoading}
        onPDFSettingsClick={() => {
          setPdfModalOpen(true)
        }}
      />
      <Box
        sx={{
          px: 2,
        }}
      >
        <DataTable
          tableHeaders={filteredHeadCells}
          tableRows={mappedData || []}
          isLoading={isLoading}
          isErrored={!!error}
          enableVerticalScroll
          maxHeight="70vh"
        />
      </Box>

      <PDFSettingsModal
        open={pdfModalOpen}
        onClose={() => {
          setPdfModalOpen(false)
        }}
        selectedAssetTypes={pdfSettings.selectedAssetTypes || []}
        setSelectedAssetTypes={handleAssetTypeChange}
      />

      {enableSendPatientWalletCard && (
        <WalletCardModal
          open={walletCardModalOpen}
          onClose={() => setWalletCardModalOpen(false)}
          patientData={selectedPatient || undefined}
        />
      )}
    </Box>
  )
}

export default PatientReportsPage
